import { h, mount, clear } from "@/lib/dom";
import { icon } from "@/lib/icons";
import { toast } from "@/components/ui";
import { topicsService, pbErrorMessage } from "@/services";
import { topicSources, termHint, type TopicSources } from "@/services/source.service";
import { transcribe, aiAvailableSync } from "@/services/ai.service";
import { markPracticed } from "@/services/streak.service";
import { addErrorCard } from "@/services/error-deck.service";
import { recordExercise } from "@/services/exercise-history.service";
import { speechService } from "@/services/speech-rounds.service";
import { createRecorder, micSupported } from "@/features/vortrag/recorder";
import { gradeSpeech, audienceLabel, AUDIENCE_KEYS, type Mode, type SpeechRating } from "@/features/vortrag/grade";
import { aiTroubleCard } from "@/features/learn/aiHealth";
import { pfRoot, pfHead, pfLoading, pfButton, pfBig, pfEmpty, onEnter } from "@/features/pfad/shell";
import { navigate, type RouteContext } from "@/app/router";
import type { Topic } from "@/types/models";

/**
 * Vortrag halten – Active Recall und Feynman in einem Ablauf.
 *
 *   Einstellungen → aufnehmen (feste Redezeit) → Transkript → Bewertung
 *   → nächste Runde mit genau einem Auftrag aus der letzten Bewertung.
 *
 * Der Kern ist die Wiederholung: erst über mehrere Runden zeigt sich, ob die
 * Erklärung besser wird. Deshalb steht in jeder Bewertung genau EIN Auftrag
 * für die nächste Runde, und die Balken beider Noten stehen über alle Runden
 * nebeneinander.
 */

const LIMITS = [60, 120, 180, 300];

export async function SpeechPage(ctx: RouteContext): Promise<HTMLElement> {
  const topicId = ctx.params.id;
  const mode = (ctx.params.mode === "feynman" ? "feynman" : "recall") as Mode;
  const toTopic = () => navigate(`/topic/${topicId}/learn`);

  const head = pfHead(toTopic);
  const stage = h("div", { class: "pf-stage" });
  const foot = h("div", { class: "pf-foot" });
  const root = pfRoot(head.el, stage, foot);
  clear(foot);

  mount(stage, h("div", { class: "pf-body" }, pfLoading("Material wird gelesen")));

  let topic: Topic;
  let src: TopicSources;
  try {
    topic = await topicsService.get(topicId);
    src = await topicSources(topic);
  } catch (err) {
    mount(stage, h("div", { class: "pf-body" }, pfEmpty("Thema nicht ladbar", pbErrorMessage(err), pfButton("Zurück", toTopic, "gray"))));
    return root;
  }

  if (!aiAvailableSync()) {
    mount(stage, h("div", { class: "pf-body" }, aiTroubleCard("Der Vortragsmodus braucht KI für Spracherkennung und Bewertung.")));
    return root;
  }
  if (!micSupported()) {
    mount(stage, h("div", { class: "pf-body" }, pfEmpty(
      "Kein Mikrofon verfügbar",
      "Dieser Browser gibt keine Aufnahme frei. In der App oder in Chrome/Safari klappt es.",
      pfButton("Zurück", toTopic, "gray")
    )));
    return root;
  }

  let limit = 120;
  let audience = "mitschueler";
  let round = 1;
  let lastFocus = "";
  const history: { round: number; content: number; method: number }[] = [];

  // Frühere Runden dieses Themas mitnehmen – dann zählt die Übung dort weiter,
  // wo man aufgehört hat, statt bei jedem Öffnen wieder bei Runde 1.
  const past = await speechService.listByTopic(topicId, mode).catch(() => []);
  if (past.length) {
    round = Math.max(...past.map((r) => r.round)) + 1;
    lastFocus = past[past.length - 1]?.rating?.nextFocus ?? "";
    for (const r of past.slice(-6)) history.push({ round: r.round, content: r.content_pct, method: r.method_pct });
  }

  renderSetup();
  return root;

  /* ----------------------------- Einstellungen ----------------------------- */

  function renderSetup(): void {
    head.set(0, 0, 1);
    clear(foot);

    const limitBtns = LIMITS.map((s) =>
      h("button", {
        type: "button",
        class: s === limit ? "on" : "",
        onclick: () => {
          limit = s;
          limitBtns.forEach((b, i) => b.classList.toggle("on", LIMITS[i] === s));
        },
      }, s < 60 ? `${s} s` : `${s / 60} min`)
    );

    const audBtns = AUDIENCE_KEYS.map((key) =>
      h("button", {
        type: "button",
        class: key === audience ? "on" : "",
        onclick: () => {
          audience = key;
          audBtns.forEach((b, i) => b.classList.toggle("on", AUDIENCE_KEYS[i] === key));
        },
      }, audienceLabel(key))
    );

    mount(stage, h("div", { class: "pf-body" },
      h("div", { class: "pf-setup" },
        h("h1", null, mode === "recall" ? "Active Recall" : "Feynman-Vortrag"),
        h("p", { class: "lead" },
          mode === "recall"
            ? `Sag frei alles, was du über „${topic.title}" weißt – ohne nachzuschauen. Die KI vergleicht mit deinem Material.`
            : `Erkläre „${topic.title}" so, dass deine Zielgruppe es versteht. Bewertet werden Inhalt UND Vortragsweise.`),

        round > 1
          ? h("div", { class: "pf-hint", style: "margin-bottom:18px" },
              `Runde ${round}. Auftrag aus der letzten Runde: ${lastFocus || "frei sprechen"}`)
          : null,

        mode === "feynman"
          ? h("div", { class: "pf-field" }, h("label", null, "Für wen erklärst du?"), h("div", { class: "pf-seg" }, ...audBtns))
          : null,

        h("div", { class: "pf-field" },
          h("label", null, "Redezeit (feste Grenze)"),
          h("div", { class: "pf-seg" }, ...limitBtns),
          h("p", { class: "muted", style: "margin:8px 0 0;font-size:12.5px" },
            "Bei Ablauf wird die Aufnahme automatisch beendet – wie bei einer echten Prüfung.")),

        history.length ? progressBlock() : null
      )
    ));
    onEnter(root, () => renderRecord());
    mount(foot, h("div", { class: "pf-foot-in" }, pfBig("Aufnahme starten", () => renderRecord())));
  }

  /** Balken je Runde – der eigentliche Zweck des Modus wird hier sichtbar. */
  function progressBlock(): HTMLElement {
    return h("div", { class: "pf-field" },
      h("label", null, "Deine bisherigen Runden"),
      h("div", { class: "vt-rounds" },
        ...history.map((r) => h("div", { class: "vt-round" },
          h("div", { class: "vt-bars" },
            h("i", { class: "c", style: `height:${Math.max(4, r.content)}%` }),
            h("i", { class: "m", style: `height:${Math.max(4, r.method)}%` })),
          h("span", { class: "vt-round-lbl" }, `R${r.round}`)))),
      h("div", { class: "vt-legend" },
        h("span", null, h("i", { class: "c" }), "Inhalt"),
        h("span", null, h("i", { class: "m" }), "Methodik")));
  }

  /* ------------------------------- Aufnahme ------------------------------- */

  function renderRecord(): void {
    const recorder = createRecorder();
    let left = limit;
    let tick = 0;
    let raf = 0;

    const timerEl = h("span", { class: "pf-timer" }, fmt(left));
    clear(head.right);
    head.right.appendChild(timerEl);

    const pulse = h("div", { class: "vt-pulse" });
    const micBtn = h("button", { class: "vt-mic", type: "button", title: "Aufnahme starten" },
      h("span", { class: "row", html: icon("mic", 42) })) as HTMLButtonElement;
    const status = h("p", { class: "vt-status" }, "Tippe auf das Mikrofon und leg los.");

    const prompt = mode === "recall"
      ? `Erzähl alles, was du über „${topic.title}" weißt.`
      : `Erkläre „${topic.title}" für ${audienceLabel(audience)}.`;

    mount(stage, h("div", { class: "pf-body" },
      h("div", { class: "vt-stage" },
        h("p", { class: "muted", style: "font-size:13px;margin:0" }, `Runde ${round} · max. ${fmt(limit)}`),
        h("h2", { class: "pf-q-title", style: "text-align:center" }, prompt),
        lastFocus && round > 1 ? h("div", { class: "pf-hint" }, `Diesmal besonders: ${lastFocus}`) : null,
        h("div", { class: "vt-mic-wrap" }, pulse, micBtn),
        status)
    ));
    clear(foot);

    let recording = false;

    micBtn.onclick = async () => {
      if (recording) { await stopAndGrade(); return; }
      try {
        await recorder.start();
      } catch {
        status.textContent = "Kein Zugriff auf das Mikrofon. Bitte die Berechtigung erlauben.";
        return;
      }
      recording = true;
      micBtn.classList.add("on");
      micBtn.title = "Aufnahme beenden";
      status.textContent = "Läuft – tippe erneut, wenn du fertig bist.";

      tick = window.setInterval(() => {
        left--;
        timerEl.textContent = fmt(Math.max(0, left));
        timerEl.classList.toggle("low", left <= 15);
        if (left <= 0) void stopAndGrade();
      }, 1000);

      const paint = () => {
        const lvl = recorder.level();
        pulse.style.transform = `scale(${1 + lvl * 0.7})`;
        pulse.style.opacity = String(0.25 + lvl * 0.5);
        raf = requestAnimationFrame(paint);
      };
      paint();
    };

    async function stopAndGrade(): Promise<void> {
      if (!recording) return;
      recording = false;
      window.clearInterval(tick);
      cancelAnimationFrame(raf);
      micBtn.disabled = true;

      let audio: Blob;
      let seconds: number;
      try {
        const out = await recorder.stop();
        audio = out.blob;
        seconds = out.seconds;
      } catch {
        toast("Die Aufnahme ist leer geblieben.", "error");
        renderSetup();
        return;
      }

      mount(stage, h("div", { class: "pf-body" }, pfLoading("Deine Aufnahme wird ausgewertet", "Erst Spracherkennung, dann Bewertung.")));
      clear(head.right);

      try {
        const text = await transcribe(audio, termHint(src));
        if (!text.trim()) {
          mount(stage, h("div", { class: "pf-body" }, pfEmpty(
            "Nichts verstanden",
            "Im Transkript steht nichts. Vielleicht war das Mikrofon stumm oder zu leise.",
            pfButton("Nochmal aufnehmen", () => renderRecord())
          )));
          return;
        }
        const rating = await gradeSpeech({
          mode, audience, round, seconds, limit, transcript: text, src,
          topicTitle: topic.title, lastFocus: lastFocus || undefined,
        });
        void speechService.save({
          topic: topicId, mode, audience, round, seconds,
          transcript: text, rating,
          content_pct: rating.contentPct, method_pct: rating.methodPct,
        }).catch(() => {});
        history.push({ round, content: rating.contentPct, method: rating.methodPct });
        recordExercise(topicId, mode === "recall" ? "recall-vortrag" : "feynman-vortrag",
          mode === "recall" ? "Active Recall (Vortrag)" : "Feynman-Vortrag",
          Math.round((rating.contentPct + rating.methodPct) / 2), 100);
        for (const m of rating.missing) addErrorCard(topicId, `Im Vortrag gefehlt: ${m}`, m);
        markPracticed();
        renderResult(rating, text, seconds);
      } catch (err) {
        mount(stage, h("div", { class: "pf-body" },
          aiTroubleCard(pbErrorMessage(err)),
          h("div", { style: "margin-top:14px;display:flex;gap:10px;justify-content:center" },
            pfButton("Nochmal versuchen", () => renderRecord(), "blue"),
            pfButton("Zurück", toTopic, "ghost"))));
      }
    }
  }

  /* ------------------------------ Auswertung ------------------------------ */

  function renderResult(r: SpeechRating, transcript: string, seconds: number): void {
    lastFocus = r.nextFocus;
    head.set(1, 0, 1);
    clear(head.right);

    const marks: Record<string, string> = { "++": "Sehr gut", "+": "Gut", o: "Geht so", "-": "Schwach", "--": "Fehlt" };

    mount(stage, h("div", { class: "pf-body" },
      h("div", { class: "pf-setup" },
        h("h1", null, `Runde ${round} ausgewertet`),

        h("div", { class: "vt-scores" },
          scoreRing("Inhalt", r.contentPct, "green"),
          scoreRing("Methodik", r.methodPct, "blue")),

        h("div", { class: "vt-summary" }, r.summary),

        r.criteria.length ? h("div", { class: "pf-field" },
          h("label", null, "Bewertung im Einzelnen"),
          h("div", { class: "vt-crit" }, ...r.criteria.map((c) =>
            h("div", { class: `vt-crit-row m${c.mark.replace(/\+/g, "p").replace(/-/g, "m")}` },
              h("span", { class: "n" }, c.name),
              h("span", { class: "mk" }, c.mark),
              h("span", { class: "cm" }, c.comment),
              h("span", { class: "lb" }, marks[c.mark] ?? ""))))) : null,

        r.covered.length ? listBlock("Das saß", r.covered, "ok") : null,
        r.missing.length ? listBlock("Das fehlte", r.missing, "err") : null,

        r.terms.length ? h("div", { class: "pf-field" },
          h("label", null, "Fachbegriffe"),
          h("div", { class: "vt-terms" }, ...r.terms.map((t) =>
            h("div", { class: "vt-term" }, h("b", null, t.term), h("span", null, t.note))))) : null,

        r.nextFocus ? h("div", { class: "vt-next" },
          h("div", { class: "lbl" }, "Auftrag für die nächste Runde"),
          h("div", { class: "txt" }, r.nextFocus)) : null,

        h("details", { class: "vt-transcript" },
          h("summary", null, `Transkript (${seconds} s)`),
          h("p", null, transcript)),

      )
    ));
    mount(foot, h("div", { class: "pf-foot-in" },
      h("div", { class: "pf-actions" },
        pfButton("Fertig", toTopic, "ghost"),
        pfBig(`Runde ${round + 1}`, () => { round++; renderSetup(); }))));
  }

  function scoreRing(label: string, pct: number, color: string): HTMLElement {
    return h("div", { class: `vt-score c-${color}` },
      h("div", { class: "ring", style: `--p:${pct}` }, h("span", null, `${pct}%`)),
      h("div", { class: "lbl" }, label));
  }

  function listBlock(title: string, items: string[], kind: "ok" | "err"): HTMLElement {
    return h("div", { class: "pf-field" },
      h("label", null, title),
      h("ul", { class: `vt-list ${kind}` }, ...items.map((t) => h("li", null, t))));
  }
}

function fmt(s: number): string {
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}
